import { RouteError } from "@/components/RouteError/RouteError";
import { UnauthorizedError } from "@/components/UnauthorizedError/UnauthorizedError";
import { API_STATUS_FORBIDDEN, API_STATUS_UNAUTHORIZED } from "@/lib/api";
import * as Sentry from "@sentry/react";
import { ApiError } from "@terralux/common/apiError";
import { type ErrorInfo, useCallback, useState } from "react";
import { ErrorBoundary as ReactErrorBoundary } from "react-error-boundary";

type Props = {
  children: React.ReactNode;
};

export function ErrorBoundary({ children }: Props) {
  const [errorInfo, setErrorInfo] = useState<ErrorInfo>();

  const handleError = useCallback((error: Error, info: ErrorInfo) => {
    setErrorInfo(info);

    if (isAccessError(error)) {
      return;
    }

    Sentry.captureReactException(error, info);
  }, []);

  return (
    <ReactErrorBoundary
      onError={handleError}
      fallbackRender={({ error, resetErrorBoundary }) => {
        if (isAccessError(error)) {
          return <UnauthorizedError />;
        }

        return (
          <RouteError
            error={error}
            stacktrace={errorInfo?.componentStack}
            onReset={() => {
              resetErrorBoundary();
              setErrorInfo(undefined);
            }}
          />
        );
      }}
    >
      {children}
    </ReactErrorBoundary>
  );
}

function isAccessError(error: Error) {
  return (
    error instanceof ApiError && (error.status === API_STATUS_UNAUTHORIZED || error.status === API_STATUS_FORBIDDEN)
  );
}
